import { supabase } from '../lib/supabase';

export const notificationService = {
  // Get all notification templates for tenant
  async getTemplates(tenantId) {
    const { data, error } = await supabase?.from('email_templates')?.select('*')?.eq('tenant_id', tenantId)?.order('category', { ascending: true });

    if (error) throw error;

    return data?.map(template => ({
      id: template?.id,
      tenantId: template?.tenant_id,
      name: template?.name,
      category: template?.category,
      subject: template?.subject,
      bodyHtml: template?.body_html,
      bodyText: template?.body_text,
      isActive: template?.is_active,
      variables: template?.variables,
      createdBy: template?.created_by,
      createdAt: template?.created_at,
      updatedAt: template?.updated_at
    }));
  },

  // Create new email template
  async createTemplate(template) {
    const { data, error } = await supabase?.from('email_templates')?.insert({
        tenant_id: template?.tenantId,
        name: template?.name,
        category: template?.category,
        subject: template?.subject,
        body_html: template?.bodyHtml,
        body_text: template?.bodyText,
        is_active: template?.isActive ?? true,
        variables: template?.variables,
        created_by: template?.createdBy
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      tenantId: data?.tenant_id,
      name: data?.name,
      category: data?.category,
      subject: data?.subject,
      bodyHtml: data?.body_html,
      bodyText: data?.body_text,
      isActive: data?.is_active,
      variables: data?.variables,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update email template
  async updateTemplate(templateId, updates) {
    const updateData = {};
    if (updates?.name !== undefined) updateData.name = updates?.name;
    if (updates?.subject !== undefined) updateData.subject = updates?.subject;
    if (updates?.bodyHtml !== undefined) updateData.body_html = updates?.bodyHtml;
    if (updates?.bodyText !== undefined) updateData.body_text = updates?.bodyText;
    if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;
    if (updates?.variables !== undefined) updateData.variables = updates?.variables;

    const { data, error } = await supabase?.from('email_templates')?.update(updateData)?.eq('id', templateId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      tenantId: data?.tenant_id,
      name: data?.name,
      category: data?.category,
      subject: data?.subject,
      bodyHtml: data?.body_html,
      bodyText: data?.body_text,
      isActive: data?.is_active,
      variables: data?.variables,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete email template
  async deleteTemplate(templateId) {
    const { error } = await supabase?.from('email_templates')?.delete()?.eq('id', templateId);

    if (error) throw error;
  },

  // Get notification preferences
  async getPreferences(tenantId) {
    const { data, error } = await supabase?.from('notification_preferences')?.select('*, email_templates(name)')?.eq('tenant_id', tenantId)?.order('notification_type', { ascending: true });

    if (error) throw error;

    return data?.map(pref => ({
      id: pref?.id,
      tenantId: pref?.tenant_id,
      notificationType: pref?.notification_type,
      enabled: pref?.enabled,
      sendHoursBefore: pref?.send_hours_before,
      templateId: pref?.template_id,
      templateName: pref?.email_templates?.name,
      createdAt: pref?.created_at,
      updatedAt: pref?.updated_at
    }));
  },

  // Update notification preference
  async updatePreference(preferenceId, updates) {
    const updateData = {};
    if (updates?.enabled !== undefined) updateData.enabled = updates?.enabled;
    if (updates?.sendHoursBefore !== undefined) updateData.send_hours_before = updates?.sendHoursBefore;
    if (updates?.templateId !== undefined) updateData.template_id = updates?.templateId;

    const { data, error } = await supabase?.from('notification_preferences')?.update(updateData)?.eq('id', preferenceId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      tenantId: data?.tenant_id,
      notificationType: data?.notification_type,
      enabled: data?.enabled,
      sendHoursBefore: data?.send_hours_before,
      templateId: data?.template_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Get notification logs
  async getLogs(tenantId, filters = {}) {
    let query = supabase?.from('notification_logs')?.select('*, customers(company_name), appointments(title), invoices(invoice_number)')?.eq('tenant_id', tenantId);

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.notificationType) {
      query = query?.eq('notification_type', filters?.notificationType);
    }

    if (filters?.customerId) {
      query = query?.eq('contact_id', filters?.customerId);
    }

    const { data, error } = await query?.order('created_at', { ascending: false })?.limit(filters?.limit || 50);

    if (error) throw error;

    return data?.map(log => ({
      id: log?.id,
      tenantId: log?.tenant_id,
      notificationType: log?.notification_type,
      recipientEmail: log?.recipient_email,
      subject: log?.subject,
      body: log?.body,
      status: log?.status,
      sentAt: log?.sent_at,
      errorMessage: log?.error_message,
      appointmentId: log?.appointment_id,
      appointmentTitle: log?.appointments?.title,
      invoiceId: log?.invoice_id,
      invoiceNumber: log?.invoices?.invoice_number,
      customerId: log?.contact_id,
      customerName: log?.contacts?.company_name || `${log?.contacts?.first_name} ${log?.contacts?.last_name}`,
      metadata: log?.metadata,
      createdAt: log?.created_at
    }));
  },

  // Send notification email
  async sendNotification(notification) {
    try {
      // Create notification log entry
      const { data: logData, error: logError } = await supabase?.from('notification_logs')?.insert({
          tenant_id: notification?.tenantId,
          notification_type: notification?.notificationType,
          recipient_email: notification?.recipientEmail,
          subject: notification?.subject,
          body: notification?.body,
          status: 'pending',
          appointment_id: notification?.appointmentId,
          invoice_id: notification?.invoiceId,
          contact_id: notification?.customerId,
          metadata: notification?.metadata || {}
        })?.select()?.single();

      if (logError) throw logError;

      // Call Supabase Edge Function to send email
      const { data: emailData, error: emailError } = await supabase?.functions?.invoke('send-email', {
        body: {
          recipientEmail: notification?.recipientEmail,
          subject: notification?.subject,
          bodyHtml: notification?.bodyHtml,
          bodyText: notification?.bodyText,
          notificationLogId: logData?.id
        }
      });

      if (emailError) throw emailError;

      // Update notification log with success status
      await supabase?.from('notification_logs')?.update({
          status: 'sent',
          sent_at: new Date()?.toISOString()
        })?.eq('id', logData?.id);

      return {
        success: true,
        logId: logData?.id,
        emailId: emailData?.emailId
      };

    } catch (error) {
      // Update notification log with failure status
      if (notification?.logId) {
        await supabase?.from('notification_logs')?.update({
            status: 'failed',
            error_message: error?.message
          })?.eq('id', notification?.logId);
      }

      throw error;
    }
  },

  // Render template with variables
  renderTemplate(template, variables) {
    let subject = template?.subject;
    let bodyHtml = template?.bodyHtml;
    let bodyText = template?.bodyText;

    // Replace variables in template
    Object.keys(variables)?.forEach(key => {
      const value = variables?.[key] || '';
      const placeholder = new RegExp(`{{${key}}}`, 'g');
      subject = subject?.replace(placeholder, value);
      bodyHtml = bodyHtml?.replace(placeholder, value);
      if (bodyText) {
        bodyText = bodyText?.replace(placeholder, value);
      }
    });

    return {
      subject,
      bodyHtml,
      bodyText
    };
  },

  // Send appointment confirmation
  async sendAppointmentConfirmation(appointmentId) {
    // Get appointment details with related data
    const { data: appointment, error: appointmentError } = await supabase?.from('appointments')?.select('*, customers(company_name, email, contact_person), services(name), user_profiles(first_name, last_name)')?.eq('id', appointmentId)?.single();

    if (appointmentError) throw appointmentError;

    // Get template
    const { data: preference } = await supabase?.from('notification_preferences')?.select('email_templates(*)')?.eq('tenant_id', appointment?.tenant_id)?.eq('notification_type', 'appointment_confirmation')?.eq('enabled', true)?.single();

    if (!preference?.email_templates) {
      throw new Error('No active template found for appointment confirmation');
    }

    // Prepare variables
    const variables = {
      customer_name: appointment?.customers?.contact_person || appointment?.customers?.company_name,
      appointment_title: appointment?.title,
      appointment_date: new Date(appointment.start_time)?.toLocaleDateString(),
      appointment_time: new Date(appointment.start_time)?.toLocaleTimeString(),
      service_name: appointment?.services?.name || 'N/A',
      location: appointment?.location || 'TBD'
    };

    // Render template
    const rendered = this.renderTemplate({
      subject: preference?.email_templates?.subject,
      bodyHtml: preference?.email_templates?.body_html,
      bodyText: preference?.email_templates?.body_text
    }, variables);

    // Send notification
    return await this.sendNotification({
      tenantId: appointment?.tenant_id,
      notificationType: 'appointment_confirmation',
      recipientEmail: appointment?.customers?.email,
      subject: rendered?.subject,
      body: rendered?.bodyText,
      bodyHtml: rendered?.bodyHtml,
      bodyText: rendered?.bodyText,
      appointmentId: appointment?.id,
      customerId: appointment?.contact_id,
      metadata: { variables }
    });
  },

  // Send invoice notification
  async sendInvoiceNotification(invoiceId) {
    // Get invoice details with related data
    const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.select('*, customers(company_name, email, contact_person), tenants(name)')?.eq('id', invoiceId)?.single();

    if (invoiceError) throw invoiceError;

    // Get template
    const { data: preference } = await supabase?.from('notification_preferences')?.select('email_templates(*)')?.eq('tenant_id', invoice?.tenant_id)?.eq('notification_type', 'invoice_sent')?.eq('enabled', true)?.single();

    if (!preference?.email_templates) {
      throw new Error('No active template found for invoice notification');
    }

    // Prepare variables
    const variables = {
      customer_name: invoice?.customers?.contact_person || invoice?.customers?.company_name,
      invoice_number: invoice?.invoice_number,
      total_amount: invoice?.total_amount?.toFixed(2),
      due_date: new Date(invoice.due_date)?.toLocaleDateString(),
      company_name: invoice?.tenants?.name
    };

    // Render template
    const rendered = this.renderTemplate({
      subject: preference?.email_templates?.subject,
      bodyHtml: preference?.email_templates?.body_html,
      bodyText: preference?.email_templates?.body_text
    }, variables);

    // Send notification
    return await this.sendNotification({
      tenantId: invoice?.tenant_id,
      notificationType: 'invoice_sent',
      recipientEmail: invoice?.customers?.email,
      subject: rendered?.subject,
      body: rendered?.bodyText,
      bodyHtml: rendered?.bodyHtml,
      bodyText: rendered?.bodyText,
      invoiceId: invoice?.id,
      customerId: invoice?.contact_id,
      metadata: { variables }
    });
  }
};